import React from "react";
import { Bike, Car, Truck } from "lucide-react";
import { Badge } from "../ui/badge";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import { cn } from "../../lib/utils";

/* Phase 200 — shared Smart Parking building blocks (admin, marshal and citizen screens). */

export const VT_META = {
  two_wheeler: { label: "Two-Wheeler", short: "2W", icon: Bike, bar: "bg-[hsl(var(--info))]" },
  four_wheeler: { label: "Four-Wheeler", short: "4W", icon: Car, bar: "bg-primary" },
  six_wheeler: { label: "Six-Wheeler", short: "6W", icon: Truck, bar: "bg-[hsl(var(--warning))]" },
};
export const VT_KEYS = Object.keys(VT_META);

export const vtLabel = (k) => VT_META[k]?.label || k;

export const VehicleTypeBadge = ({ type, short = false, className }) => {
  const m = VT_META[type] || { label: type, short: type, icon: Car };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={cn("gap-1 font-medium", className)} data-testid={`vehicle-type-badge-${type}`}>
      <Icon className="h-3 w-3" /> {short ? m.short : m.label}
    </Badge>
  );
};

export const BOOKING_STATUS = {
  reserved: "bg-sky-50 text-sky-700 border-sky-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  no_show: "bg-amber-50 text-amber-700 border-amber-200",
  expired: "bg-amber-50 text-amber-700 border-amber-200",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  pending_remittance: "bg-amber-50 text-amber-700 border-amber-200",
  remitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-sky-50 text-sky-700 border-sky-200",
  waiting: "bg-sky-50 text-sky-700 border-sky-200",
  notified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  open: "bg-red-50 text-red-700 border-red-200",
  abandoned: "bg-slate-100 text-slate-500 border-slate-200",
};

export const PStatus = ({ status, className }) => {
  const s = String(status || "").toLowerCase();
  return (
    <Badge variant="outline" className={cn(BOOKING_STATUS[s] || "bg-slate-100 text-slate-600 border-slate-200", "capitalize font-medium", className)} data-testid={`parking-status-${s}`}>
      {s.replace(/_/g, " ")}
    </Badge>
  );
};

/** Stacked availability bars per vehicle type for one location's occupancy object. */
export const AvailabilityBars = ({ occupancy, compact = false, testId }) => {
  if (!occupancy) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className={cn("space-y-1", compact ? "min-w-[140px]" : "min-w-[200px]")} data-testid={testId}>
      {VT_KEYS.map((k) => {
        const cap = occupancy.capacity?.[k] || 0;
        if (!cap) return null;
        const occ = occupancy.occupied?.[k] || 0;
        const res = occupancy.reserved?.[k] || 0;
        const avail = occupancy.available?.[k] ?? Math.max(cap - occ - res, 0);
        const m = VT_META[k];
        return (
          <div key={k} className="flex items-center gap-2">
            <span className="text-[11px] w-7 text-muted-foreground font-medium">{m.short}</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex">
              <div className={cn("h-full", m.bar)} style={{ width: `${(occ / cap) * 100}%` }} title={`${occ} parked`} />
              <div className={cn("h-full opacity-50", m.bar)} style={{ width: `${(res / cap) * 100}%` }} title={`${res} reserved`} />
            </div>
            <span className="text-[11px] tabular-nums w-14 text-right" data-testid={`${testId || "avail"}-${k}`}>
              <span className={avail === 0 ? "text-destructive font-semibold" : "font-semibold"}>{avail}</span>
              <span className="text-muted-foreground">/{cap}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const occupancyTone = (pct) => (pct >= 90 ? "text-destructive" : pct >= 70 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--success))]");
export const occupancyColor = (pct) => (pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : "#0f766e");

export const ParkingLiveBadge = ({ className, testId = "parking-live-badge" }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 rounded-full border border-border bg-background/80 px-2 py-0.5 text-[11px] font-medium text-muted-foreground select-none", className)}>
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--success))] opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
          </span>
          Live
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs">Occupancy, bookings and collections refresh automatically every ~10 seconds and whenever you return to this tab.</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const fmtHours = (h) => (!h ? "—" : h.closed ? `Closed${h.reason ? ` (${h.reason})` : ""}` : `${to12(h.open)} – ${to12(h.close)}`);

export const to12 = (t) => {
  if (!t) return "";
  const [hh, mm] = String(t).split(":");
  const h = Number(hh);
  if (Number.isNaN(h)) return t;
  return `${h % 12 === 0 ? 12 : h % 12}:${mm} ${h < 12 ? "AM" : "PM"}`;
};

export const fmtMinutes = (m) => {
  const n = Number(m || 0);
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60), r = n % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
};

export const MODE_LABELS = { upi: "UPI", card: "Card", netbanking: "Net banking", wallet: "Wallet", fastag: "FASTag", pos: "POS", cash: "Cash", bank_transfer: "Bank transfer", cheque: "Cheque" };
export const modeLabel = (m) => MODE_LABELS[m] || (m ? m.toUpperCase() : "—");
